import { z } from 'zod';
import { BillingCycleSchema, CurrencySchema, PayLangSchema, SkuSchema } from './schemas';

/**
 * Página pública de pagamento `/{lang}/pay/{attemptId}` (#752, A3 do #748).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3: contrato da venda pelo Asaas e da
 * página `/pay` pública).
 *
 * Por quê: o link da venda pelo Asaas aponta para uma página NOSSA, não para o
 * gateway: é lá que o cliente escolhe o meio (cartão, Pix Automático,
 * boleto), e só então o objeto do Asaas é criado — o checkout de cartão
 * expira em no máximo 24 h, e o link da venda vive 7 dias.
 *
 * A página não tem login. O id da tentativa (UUID v4, 122 bits aleatórios) é
 * a capacidade de acesso, e o que volta é uma PROJEÇÃO: nada de
 * `sales_origin`, `floor_snapshot`, comissão ou dado do lead.
 *
 * Contrato: só schemas e uma constante; nenhum efeito.
 *
 * Usado por: Functions — `billing/pay/payCallables.ts` (entrada/saída das
 * callables públicas), `billing/pay/payAttemptView.ts` (projeção),
 * `billing/asaas/payOption.ts` (`PayMethod`/`PayNextStep`),
 * `billing/asaas/saleLink.ts` (`ASAAS_SALE_LINK_TTL_MS`); web `apps/app` —
 * `features/billing/PayPage.tsx` e `lib/callables.ts`, que validam a resposta
 * com os MESMOS schemas antes de usá-la (uma resposta fora do contrato vira
 * `reportNonFatal` e erro genérico, nunca um redirect cego). Android e iOS não
 * têm a tela (spec §10) e não importam este módulo.
 *
 * Arquitetura: o servidor é a fonte da verdade do estado da tentativa
 * (`/checkoutAttempts/{uuid}`, server-only); a página só projeta. Nada aqui
 * carrega tenant, split ou id do gateway — é a fronteira pública.
 *
 * Dados — resposta de `getPayAttempt` numa tentativa aberta com o Pix já
 * escolhido (anonimizado; valores em CENTS, instantes em epoch MS):
 *   {
 *     "state": "OPEN",
 *     "lang": "pt",
 *     "view": {
 *       "companyName": "Restaurante Exemplo",
 *       "billingCycle": "monthly",
 *       "currency": "BRL",
 *       "totalCents": 24890,
 *       "items": [
 *         { "sku": "BASE", "amountCents": 19990, "name": null },
 *         { "sku": "MOD_SCALE", "amountCents": 4900, "name": "Balança" }
 *       ],
 *       "methods": ["card", "pix", "boleto"],
 *       "expiresAt": 1790600000000,
 *       "next": { "kind": "pix_qr", "method": "pix", "payload": "00020101…",
 *                 "encodedImage": "iVBORw0KGgo…", "expiresAt": 1790003600000 }
 *     }
 *   }
 * Nos estados `EXPIRED`/`UNAVAILABLE`, `view` é `null`.
 *
 * Falhas conhecidas:
 *  - 2026-09-27 15:49 +0200 (#752, A3, F-L1): a página seguia a URL do
 *    `redirect` sem validar o host e fazia `.parse` sem report; hoje servidor
 *    e página só aceitam `https://*.asaas.com` e a página usa `safeParse`.
 *  - 2026-09-27 15:48 +0200 (#752, A3, B-H3/F-M4): boleto escolhido no fim do
 *    link vencia depois dele e era retomado para sempre; a tentativa com
 *    boleto hoje vence no vencimento + 5 dias de compensação.
 *
 * Dicas: `payPage.test.ts` cobre os schemas; a página em `PayPage.test.tsx`.
 * Spec da tela: `docs/screens/pay-public.md`; desenho: `docs/billing/asaas.md`
 * ("Venda (A3, #752)").
 *
 * Issues: #748 (épico), #752 (A3), #755 (A6: anual parcelado), #756 (A7:
 * modo live).
 *
 * Histórico:
 *  - 2026-09-27 14:28 +0200 — (#752) contrato criado.
 *  - 2026-09-27 15:02 +0200 — (#752) `items[].name` no idioma da página.
 */

/**
 * Meios que a página oferece (G5). O anual parcelado não é um meio à parte:
 * é o `card` com `installments ≥ 2` em `ChoosePayOptionInput` (#755, A6).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Vocabulário da PÁGINA, minúsculo e sem acoplamento ao gateway: o servidor
 * traduz de/para `payment_method_types` da tentativa (`PaymentMethodType.CARD`/
 * `PIX`/`BOLETO`, cujos valores por acaso também são `card`/`pix`/`boleto`)
 * e para o meio do Asaas (checkout RECURRENT, autorização de Pix Automático,
 * assinatura `BOLETO`).
 *
 * Usado por: `PayNextStepSchema`, `GetPayAttemptOutput.view.methods`,
 * `ChoosePayOptionInput` (este módulo); o tipo `PayMethod` em `PayPage.tsx`
 * (web), `payOption.ts` e `payAttemptView.ts` (Functions).
 */
export const PayMethodSchema = z.enum(['card', 'pix', 'boleto']);
export type PayMethod = z.infer<typeof PayMethodSchema>;

/**
 * UUID v4 minúsculo — o formato de `crypto.randomUUID()`, que é como o
 * servidor gera o id de `checkoutAttempts/{uuid}` na venda pelo Asaas.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * O regex é estrito de propósito (versão `4` e variante `8|9|a|b` fixas):
 * a página recusa um id fora do formato SEM chamar o servidor, e o servidor
 * recusa antes de ler o Firestore — ids de outras origens (Stripe `cs_…`,
 * ids antigos) nunca chegam à consulta.
 *
 * Usado por: `GetPayAttemptInput`, `ChoosePayOptionInput` (este módulo);
 * `PayPage.tsx` (web, filtro antes da rede); `payPage.test.ts`.
 *
 * Arquitetura: o id É a credencial da página (122 bits aleatórios, sem
 * login); por isso não vai para a telemetria (`apps/app/src/lib/events.ts`).
 *
 * Dados: `"00000000-0000-4000-8000-000000000000"` (anonimizado).
 */
export const PayAttemptIdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);

/**
 * Entrada da callable pública `getPayAttempt` — só o id; não há sessão.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Usado por: `payCallables.ts` (Functions, `parseOrThrow`) e
 * `apps/app/src/lib/callables.ts` (web, `input.parse` antes da rede).
 *
 * Dados: `{ "attemptId": "00000000-0000-4000-8000-000000000000" }`.
 */
export const GetPayAttemptInput = z.object({ attemptId: PayAttemptIdSchema });
export type GetPayAttemptInput = z.infer<typeof GetPayAttemptInput>;

/**
 * O próximo passo do cliente depois de escolher o meio: ir para uma página do
 * gateway, ou pagar o QR do Pix Automático aqui mesmo (o Asaas não hospeda a
 * autorização).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Usado por: `GetPayAttemptOutput.view.next` e `ChoosePayOptionOutput` (este
 * módulo); o tipo `PayNextStep` em `payOption.ts` (Functions, monta) e
 * `PayPage.tsx` (web, consome).
 *
 * Dados — `redirect` anonimizado:
 *   { "kind": "redirect", "method": "card",
 *     "url": "https://sandbox.asaas.com/checkoutSession/show/00000000-0000-4000-8000-000000000000" }
 * (o host `sandbox.asaas.com` e o path `/checkoutSession/show/<uuid>` são os
 * medidos no spike A0, #749, pergunta 9; o boleto usa `/i/<id>`).
 */
export const PayNextStepSchema = z.discriminatedUnion('kind', [
  /**
   * Cartão (checkout hospedado) e boleto (`invoiceUrl` da 1ª cobrança). A URL
   * é do Asaas, e o servidor confere o host antes de gravá-la; a página confere
   * de novo (`https://*.asaas.com`) antes do `location.assign`, porque este
   * schema só garante "é uma URL", não "é do gateway" (#752, A3, F-L1).
   */
  z.object({ kind: z.literal('redirect'), method: PayMethodSchema, url: z.url() }),
  /**
   * Pix Automático: o Asaas não hospeda a autorização, então o QR é mostrado
   * na própria página, que consulta `getPayAttempt` até o webhook do 1º
   * pagamento fechar a tentativa.
   */
  z.object({
    kind: z.literal('pix_qr'),
    method: z.literal('pix'),
    /** Copia-e-cola. */
    payload: z.string().min(1),
    /** PNG em base64, sem o prefixo `data:`. */
    encodedImage: z.string().min(1),
    /**
     * Epoch ms em que o QR deixa de valer (o `expirationDate` do Asaas, ou o
     * prazo que o servidor pediu quando o formato da data não é reconhecido).
     * `null` quando desconhecido. Depois dele a página volta à escolha.
     */
    expiresAt: z.number().int().nullable(),
  }),
]);
export type PayNextStep = z.infer<typeof PayNextStepSchema>;

/**
 * `OPEN` aceita escolha; `COMPLETED` já foi pago; `EXPIRED` venceu ou foi
 * substituído por um link novo; `UNAVAILABLE` cobre o que não é da página
 * (id inexistente, tentativa do Stripe) — a página não distingue, para não
 * virar oráculo de ids.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Contrato: `EXPIRED` também cobre o tenant excluído (`payAttemptView.ts`).
 *
 * Usado por: `GetPayAttemptOutput.state`; `PayPage.tsx` (web: decide entre a
 * escolha e o cartão de estado) e o evento `pay_page_viewed { state }`.
 */
export const PayAttemptStateSchema = z.enum(['OPEN', 'COMPLETED', 'EXPIRED', 'UNAVAILABLE']);
export type PayAttemptState = z.infer<typeof PayAttemptStateSchema>;

/**
 * Saída de `getPayAttempt`. `state` + `lang` vêm sempre; `view` só quando há
 * resumo a mostrar.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Por quê: projeção por LISTA BRANCA (`readPayAttemptView`) — um campo novo na
 * tentativa não vaza para a página pública sem alguém acrescentá-lo aqui.
 *
 * Usado por: `payAttemptView.ts`/`payCallables.ts` (Functions, produz);
 * `PayPage.tsx` (web, `safeParse` na carga e a cada rodada do polling do Pix).
 *
 * Dados: exemplo no cabeçalho do módulo.
 *
 * Histórico:
 *  - 2026-09-27 14:28 +0200 — (#752) criado.
 *  - 2026-09-27 15:02 +0200 — (#752) `items[].name`.
 */
export const GetPayAttemptOutput = z.object({
  /** Ver `PayAttemptStateSchema`. */
  state: PayAttemptStateSchema,
  /**
   * Idioma gravado na tentativa quando o link foi gerado (o do tenant). O
   * servidor devolve `en` no `UNAVAILABLE`, porque aí não há tentativa de onde
   * tirar o idioma. A UI da página segue o `lang` do PATH (`PayLangRoute`),
   * não este campo — o link já nasce com o idioma certo no path.
   */
  lang: PayLangSchema,
  /** O resto só vem quando há o que mostrar (`OPEN`/`COMPLETED`). */
  view: z
    .object({
      /** Nome do tenant, para o cliente reconhecer que o link é dele. */
      companyName: z.string(),
      /** Ciclo congelado na tentativa. */
      billingCycle: BillingCycleSchema,
      /**
       * Máximo de parcelas que a página oferece no cartão (#755, A6): 12 no
       * anual com cartão liberado, 1 no resto (à vista, sem seletor).
       * `.default(1)`: servidor anterior à A6 não manda o campo, e a página
       * nova não pode quebrar o `safeParse` contra ele durante o deploy.
       * Histórico: 2026-09-28 09:53 +0200 — (#755) criado.
       */
      maxInstallments: z.number().int().min(1).max(12).default(1),
      /** Na venda pelo Asaas é sempre BRL (o Asaas só cobra em real). */
      currency: CurrencySchema,
      /** Total por ciclo, em cents, já com o preço negociado na Contratação. */
      totalCents: z.number().int().nonnegative(),
      /**
       * `name` é o do catálogo no idioma da página (a página é pública e não lê
       * o catálogo); `null` no BASE e em módulo sem nome — a página usa o rótulo.
       */
      items: z.array(
        z.object({ sku: SkuSchema, amountCents: z.number().int().nonnegative(), name: z.string().nullable() }),
      ),
      /** Os meios que a Contratação liberou para esta venda, na ordem de exibição. */
      methods: z.array(PayMethodSchema),
      /** Epoch ms do fim do link (`ASAAS_SALE_LINK_TTL_MS` a partir da geração). */
      expiresAt: z.number().int().nullable(),
      /** A escolha já feita e ainda válida — a página retoma dela. */
      next: PayNextStepSchema.nullable(),
    })
    .nullable(),
});
export type GetPayAttemptOutput = z.infer<typeof GetPayAttemptOutput>;

/**
 * Entrada da callable pública `choosePayOption`.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Contrato: o servidor ainda recusa com `method_not_offered` um meio válido
 * que não esteja nos `methods` da tentativa; as demais recusas (`details.reason`)
 * estão em `KNOWN_ERRORS` da `PayPage.tsx` e na spec §5.
 *
 * Usado por: `payCallables.ts` (Functions); `apps/app/src/lib/callables.ts`.
 *
 * Dados: `{ "attemptId": "00000000-0000-4000-8000-000000000000", "method": "boleto" }`;
 * parcelado: `{ …, "method": "card", "installments": 6 }`.
 */
export const ChoosePayOptionInput = z.object({
  attemptId: PayAttemptIdSchema,
  method: PayMethodSchema,
  /**
   * Parcelas no cartão (#755, A6). Ausente = 1 (à vista). ≥ 2 só no anual com
   * cartão, até `view.maxInstallments`; o servidor recusa o resto com
   * `installments_not_offered`. O parcelado não passa pelo checkout hospedado
   * (o `INSTALLMENT` do Asaas exige `DETACHED`, medido na A6): vira
   * `POST /payments` com `installmentCount`, e o cliente paga no `invoiceUrl`.
   * Histórico: 2026-09-28 09:53 +0200 — (#755) criado.
   */
  installments: z.number().int().min(1).max(12).optional(),
});
export type ChoosePayOptionInput = z.infer<typeof ChoosePayOptionInput>;

/**
 * A escolha devolve o mesmo passo que a retomada (`view.next`) mostraria —
 * por isso é o mesmo schema: a página trata "acabei de escolher" e "voltei
 * depois" pelo mesmo caminho.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Usado por: `payCallables.ts` (Functions); `PayPage.tsx` (web, `safeParse`).
 */
export const ChoosePayOptionOutput = PayNextStepSchema;
export type ChoosePayOptionOutput = z.infer<typeof ChoosePayOptionOutput>;

/**
 * Validade do link da venda pelo Asaas: o boleto vence em 3 dias e cabe dentro.
 * O prazo é do LINK (a nossa tentativa), não dos objetos do gateway: o
 * checkout de cartão dura no máximo 24 h e é recriado a cada escolha fora da
 * janela de reaproveitamento, e o QR do Pix tem prazo próprio. Um boleto
 * emitido no fim da validade ainda é honrado — a tentativa com boleto só vence
 * depois do vencimento + 5 dias de compensação (auditoria da A3, #752).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Contrato: milissegundos (7 × 24 h), somados ao `now` do SERVIDOR na geração.
 *
 * Usado por: `apps/functions/src/billing/asaas/saleLink.ts` (`expires_at` da
 * tentativa); o valor chega à página como `view.expiresAt`.
 *
 * Falhas conhecidas: 2026-09-27 15:48 +0200 (#752, A3, B-H3/F-M4) — boleto
 * escolhido no 5º–7º dia vencia depois do link, e o passe 9 o apagava pago e
 * não compensado; o prazo do boleto passou a valer por conta própria.
 *
 * Issues: #752 (A3), W-L6 no registro da auditoria (o `ABANDONED` do CRM em 5
 * dias contra este prazo de 7 — aceito).
 */
export const ASAAS_SALE_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;
